import "@/App.css";
import { useState, useEffect } from "react";
import { 
  Shield, 
  Wrench, 
  Target, 
  FileText, 
  ChevronDown,
  AlertTriangle,
  Gauge,
  Crosshair,
  ClipboardList,
  Heart,
  Settings,
  BookOpen,
  Clock,
  Zap
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";

// Constants
const CTA_LINK = "http://staydangerousmanual.com/";
const DEAL_PRICE = "$27";
const REGULAR_MANUAL_PRICE = "$37";
const REGULAR_BUNDLE_PRICE = "$47";

// Evergreen 3-day countdown - resets when it reaches 0
const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

// Countdown Hook
const useCountdown = () => {
  const getStoredEndTime = () => {
    const stored = localStorage.getItem('dealEndTime');
    if (stored) {
      const endTime = parseInt(stored, 10);
      if (endTime > Date.now()) {
        return endTime;
      }
    }
    // Set new 3-day countdown
    const newEndTime = Date.now() + THREE_DAYS_MS;
    localStorage.setItem('dealEndTime', newEndTime.toString());
    return newEndTime;
  };

  const [targetTimestamp, setTargetTimestamp] = useState(getStoredEndTime);

  const calculateTimeLeft = () => {
    const difference = targetTimestamp - Date.now();
    if (difference <= 0) {
      // Reset to 3 days
      const newEndTime = Date.now() + THREE_DAYS_MS;
      localStorage.setItem('dealEndTime', newEndTime.toString());
      setTargetTimestamp(newEndTime);
      return {
        days: 3,
        hours: 0,
        minutes: 0,
        seconds: 0,
        expired: false
      };
    }
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
      expired: false
    };
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, [targetTimestamp]);

  return timeLeft;
};

// Countdown Timer Component
const CountdownTimer = () => {
  const timeLeft = useCountdown();

  if (timeLeft.expired) return null;

  return (
    <div className="bg-zinc-900 border border-yellow-500/30 p-3 sm:p-4 mb-4 sm:mb-6">
      <div className="flex items-center gap-2 mb-2 sm:mb-3">
        <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500" />
        <span className="text-[10px] sm:text-xs text-yellow-500 uppercase tracking-wider font-bold">Limited Time Offer Ends In</span>
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        <div>
          <div className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>{timeLeft.days}</div>
          <div className="text-[9px] sm:text-xs text-zinc-500 uppercase">Days</div>
        </div>
        <div>
          <div className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>{timeLeft.hours}</div>
          <div className="text-[9px] sm:text-xs text-zinc-500 uppercase">Hours</div>
        </div>
        <div>
          <div className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>{timeLeft.minutes}</div>
          <div className="text-[9px] sm:text-xs text-zinc-500 uppercase">Mins</div>
        </div>
        <div>
          <div className="text-xl sm:text-2xl font-bold text-yellow-500" style={{ fontFamily: 'Oswald, sans-serif' }}>{timeLeft.seconds}</div>
          <div className="text-[9px] sm:text-xs text-zinc-500 uppercase">Secs</div>
        </div>
      </div>
    </div>
  );
};

// Image URLs from user assets
const IMAGES = {
  logo: "https://customer-assets.emergentagent.com/job_tactical-manual-hub/artifacts/mf4rc5rw_file_00000000cf487206b0cd273bc3a2cf82.png",
  manualCover: "https://customer-assets.emergentagent.com/job_tactical-manual-hub/artifacts/qt04xajq_T%C3%BAatha%20D%C3%A9%20%281400%20x%201400%20px%29%20%281400%20x%201400%20px%29_20260228_090621_0000.jpg",
  heroWalking: "https://customer-assets.emergentagent.com/job_tactical-manual-hub/artifacts/v1l6bs7f_Page%20111.png",
  heroTactical: "https://customer-assets.emergentagent.com/job_tactical-manual-hub/artifacts/8r84gfjh_file_000000000b1471fdaedc2e5817951af3.png",
  comparison: "https://customer-assets.emergentagent.com/job_tactical-manual-hub/artifacts/sg3e1sji_file_00000000997c71fd8f82af953ef7f306.png",
};

// Service Bulletins Data - From the Manual
const serviceBulletins = [
  {
    id: "SB-001",
    title: "Protocol 30 Mission Plan",
    description: "30-day tactical implementation schedule. The 4-week recalibration to clear the rust and fire the engine.",
    icon: Target,
  },
  {
    id: "SB-002", 
    title: "Fuel Logistics Manifest",
    description: "The Three Laws: Protein Anchor, Carbohydrate Toggle, Green Buffer. Field Ration List included.",
    icon: Gauge,
  },
  {
    id: "SB-003",
    title: "The Daily Launch Diagnostic",
    description: "Morning system check: Neck, Overhead Reach, Deep Squat. Your dashboard warning lights.",
    icon: ClipboardList,
  },
  {
    id: "SB-004",
    title: "Recovery & Maintenance Blueprint",
    description: "Sleep protocols, the caffeine curfew, field maintenance kit. Motion is lotion for old joints.",
    icon: Settings,
  },
  {
    id: "SB-005",
    title: "The 15 Rules",
    description: "From 'Kill the Ghost' to 'Asset vs Liability'. The non-negotiable standards. Print. Post. Live by.",
    icon: FileText,
  },
];

// FAQ Data - From the Manual
const faqData = [
  {
    question: "Who is this manual for?",
    answer: "This is for men over 40 who refuse to accept the 'soft decline' but are smart enough to know they can't train like a 25-year-old. Whether you're a veteran with old injuries or the 'New Guard' who never played contact sports—you belong here. The moment you pick up the weight and refuse to quit, you are in the tribe."
  },
  {
    question: "What are the 'Big Five' movement patterns?",
    answer: "The Squat (knee dominant), The Hinge (glutes & hamstrings), The Press (shoulders), The Pull (back), and The Carry (full body). These are the movements the human body was designed to do. We train patterns, not muscle groups—because in the real world, your body doesn't isolate muscles when you're carrying a sleepy child or moving a fridge."
  },
  {
    question: "What if I have old injuries?",
    answer: "The manual includes a full If/Then Playbook for adaptations. Shoulder pinches on presses? Switch to neutral-grip dumbbells. Knees complain on deep squats? Use a box or split squats. Back talks during deadlifts? Move to the trap bar. The rule: when pain shows up, pivot immediately—keep moving, just swap in the safest tool."
  },
  {
    question: "I'm not an ex-athlete. Is this for me?",
    answer: "Absolutely. The manual addresses the 'New Guard' specifically—men who didn't play contact sports or march with a pack. You actually have an advantage: no scar tissue, no ego whispering about what you 'used to' lift. You're a low-mileage vehicle in a high-performance garage."
  },
  {
    question: "What's the difference between this and other fitness programs?",
    answer: "This isn't a fitness program—it's a Field Manual. No motivation speeches. No 'smash yourself into the ground' mentality. We prioritize Consistency over Intensity because at 40+, if you train at 100% and need 4 days to recover, you're only training once a week. That's not enough volume to stay dangerous."
  },
  {
    question: "What's included in the Tactical Bundle?",
    answer: "The 112-page STAY DANGEROUS AFTER 40 Field Manual plus 5 printable Service Bulletins: Protocol 30 Mission Plan, Fuel Logistics Manifest (with the Field Ration List), The Daily Launch Diagnostic, Recovery & Maintenance Blueprint, and The 15 Rules. Everything you need to stop the decline and stay operational."
  },
];

// Header Component
const Header = () => {
  return (
    <header className="absolute top-0 left-0 right-0 z-50 py-4 sm:py-6">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between">
          <img 
            src={IMAGES.logo} 
            alt="Refuse Harmless" 
            className="h-14 sm:h-20 lg:h-24 w-auto"
            data-testid="header-logo"
          />
          <a 
            href={CTA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:block"
            data-testid="header-cta"
          >
            <Button 
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-xs sm:text-sm px-4 sm:px-6 py-2 sm:py-3 rounded-none border-2 border-yellow-500 uppercase tracking-wider"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              Get the Manual
            </Button>
          </a>
        </div>
      </div>
    </header>
  );
};

// Hero Section Component
const HeroSection = () => {
  return (
    <section className="min-h-screen flex items-center relative overflow-hidden bg-black">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url(${IMAGES.heroTactical})`,
          filter: 'brightness(0.3)'
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
      
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 pt-24 sm:pt-32 lg:pt-36 pb-16 sm:pb-20 relative z-10">
        <div className="max-w-3xl">
          {/* Status indicator */}
          <div className="flex items-center gap-3 mb-6 sm:mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
            <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
            <span className="serial-number text-[10px] sm:text-xs">SYSTEM STATUS: OPERATIONAL</span>
          </div>
          
          {/* Main headline */}
          <h1 
            className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white leading-[1] sm:leading-[0.95] mb-4 sm:mb-6 animate-slide-up"
            style={{ animationDelay: '0.2s' }}
            data-testid="hero-headline"
          >
            Are you an <span className="text-yellow-500">Asset</span> or a <span className="text-zinc-500">Liability</span>?
          </h1>
          
          {/* Subheadline */}
          <div 
            className="text-base sm:text-lg lg:text-xl text-zinc-200 mb-6 sm:mb-8 leading-relaxed max-w-2xl animate-slide-up space-y-4"
            style={{ animationDelay: '0.3s' }}
          >
            <p><span className="text-yellow-500 font-bold">In a crisis, there is no middle ground.</span></p>
            <p>Most men over 40 are rotting on the sidelines—harmless, rusted, and unprepared for the moment the world goes sideways.</p>
            <p>The Asset is the man who walks into chaos and makes the collective heart rate of the room drop. <span className="text-white font-bold">He doesn't need saving; he is the one who saves.</span></p>
            <p>Stop being a spectator. <span className="text-yellow-500">Rebuild your engine, restore your standards, and get back to the game.</span></p>
          </div>
          
          {/* CTA Button */}
          <div className="animate-slide-up" style={{ animationDelay: '0.4s' }}>
            {/* Urgency Banner */}
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-4 h-4 text-yellow-500 flex-shrink-0" />
              <span className="text-xs sm:text-sm text-yellow-500 font-bold uppercase tracking-wider">
                3-Day Deal: Full Bundle for {DEAL_PRICE} (Saves $20)
              </span>
            </div>
            <a 
              href={CTA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta"
              className="block"
            >
              <Button 
                className="btn-swipe w-full sm:w-auto bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-sm sm:text-lg px-6 sm:px-8 py-5 sm:py-6 rounded-none border-2 border-yellow-500 uppercase tracking-wider"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                <Shield className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Secure the Tactical Bundle ({DEAL_PRICE})
              </Button>
            </a>
          </div>
          
          {/* Quick stats */}
          <div className="grid grid-cols-3 gap-4 sm:gap-6 mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-zinc-800 animate-slide-up" style={{ animationDelay: '0.5s' }}>
            <div>
              <div className="text-2xl sm:text-3xl font-bold text-yellow-500" style={{ fontFamily: 'Oswald, sans-serif' }}>112</div>
              <div className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-wider">Pages</div>
            </div>
            <div>
              <div className="text-2xl sm:text-3xl font-bold text-yellow-500" style={{ fontFamily: 'Oswald, sans-serif' }}>5</div>
              <div className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-wider">Service Bulletins</div>
            </div>
            <div>
              <div className="text-2xl sm:text-3xl font-bold text-yellow-500" style={{ fontFamily: 'Oswald, sans-serif' }}>30</div>
              <div className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-wider">Day Protocol</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Scroll indicator - hidden on mobile */}
      <div className="hidden sm:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 animate-bounce">
        <span className="serial-number">SCROLL FOR BRIEFING</span>
        <ChevronDown className="w-5 h-5 text-yellow-500" />
      </div>
    </section>
  );
};

// Problem Section Component
const ProblemSection = () => {
  return (
    <section className="bg-zinc-950 py-12 sm:py-20 lg:py-32 relative" data-testid="problem-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-center">
          {/* Left - Text content */}
          <div className="order-2 lg:order-1">
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
              <span className="serial-number text-[10px] sm:text-xs">DIAGNOSTIC REPORT // SECTION 01</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight">
              The <span className="text-yellow-500">Problem</span>
            </h2>
            
            <div className="gold-line mb-6 sm:mb-8" />
            
            <div className="space-y-4 sm:space-y-6 text-sm sm:text-base text-zinc-400 leading-relaxed">
              <p>
                <span className="text-white font-bold">Most men over 40 fall into two categories:</span> Those who surrender to the "soft decline"—accepting weight gain, fatigue, and deterioration as inevitable. And those who break themselves trying to train like they're still 25.
              </p>
              
              <p>
                Both paths lead to the same destination: <span className="text-yellow-500 font-bold">becoming harmless.</span>
              </p>
              
              <div className="tactical-card p-4 sm:p-6 my-6 sm:my-8 corner-marks">
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  THE DANGEROUS THIRD WAY
                </h3>
                <p className="text-sm sm:text-base text-zinc-400">
                  We are no longer building a show pony—we are maintaining a warhorse. We don't need racing stripes; we need a chassis that doesn't rust and an engine that turns over every single time.
                </p>
              </div>
              
              <p>
                The difference between a rookie and a veteran isn't just skill level. It's how they treat their equipment. <span className="text-white">Oil the hinges. Do the work. Stay dangerous.</span>
              </p>
            </div>
          </div>
          
          {/* Right - Comparison Image */}
          <div className="order-1 lg:order-2 relative">
            <div className="aspect-[4/3] sm:aspect-square relative overflow-hidden">
              <img 
                src={IMAGES.comparison}
                alt="Showroom vs Field - The difference in approach"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 serial-number text-[10px] sm:text-xs">FIG. 01 // THE ASSET</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Product Section Component
const ProductSection = () => {
  return (
    <section className="bg-black py-12 sm:py-20 lg:py-32 relative" data-testid="product-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-8 sm:mb-16">
          <div className="flex items-center justify-center gap-3 mb-4 sm:mb-6">
            <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
            <span className="serial-number text-[10px] sm:text-xs">PRODUCT SPECIFICATION // SECTION 02</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            The <span className="text-yellow-500">Field Manual</span>
          </h2>
          
          <div className="gold-line mx-auto" />
        </div>
        
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-center">
          {/* Manual Cover - Using actual image */}
          <div className="flex justify-center">
            <div className="relative group">
              <img 
                src={IMAGES.manualCover}
                alt="Stay Dangerous After 40 - Field Manual"
                className="w-64 sm:w-80 lg:w-96 shadow-2xl shadow-yellow-500/10 transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute -bottom-3 -right-3 sm:-bottom-4 sm:-right-4 w-full h-full border-2 border-yellow-500/30 -z-10" />
            </div>
          </div>
          
          {/* Manual Details */}
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6" style={{ fontFamily: 'Oswald, sans-serif' }}>
              112-PAGE OPERATIONAL MANUAL
            </h3>
            
            <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
              {[
                "The Big Five movement patterns (Squat, Hinge, Press, Pull, Carry)",
                "The Daily Launch: Morning diagnostics & pre-workout ignition",
                "Fuel Logistics: The Three Laws of eating real food",
                "The 4-Week Recalibration for returning to the iron",
                "Situational Tactics: Travel, desk jobs, crisis protocols",
                "Field Maintenance: Managing the creaks without surgery",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-sm sm:text-base text-zinc-400">
                  <Crosshair className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500 mt-1 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            
            <CountdownTimer />
            
            <div className="tactical-card p-4 sm:p-6 mb-6 sm:mb-8">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <Zap className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500" />
                <span className="text-[10px] sm:text-xs text-yellow-500 uppercase tracking-wider font-bold">Limited Time Offer</span>
              </div>
              <div className="flex items-center justify-between mb-3 sm:mb-4 pb-3 sm:pb-4 border-b border-zinc-700">
                <div>
                  <div className="text-xs sm:text-sm text-zinc-400 line-through">Regular: {REGULAR_BUNDLE_PRICE}</div>
                  <div className="text-3xl sm:text-4xl font-bold text-yellow-500" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    {DEAL_PRICE}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] sm:text-sm text-zinc-500 uppercase">You Get</div>
                  <div className="text-sm sm:text-lg text-white font-bold">Manual + 5 Bulletins</div>
                </div>
              </div>
              <div className="text-[10px] sm:text-xs text-zinc-500">
                <span className="text-zinc-400">After deal ends:</span> {REGULAR_MANUAL_PRICE} manual / {REGULAR_BUNDLE_PRICE} full bundle
              </div>
            </div>
            
            <a 
              href={CTA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="product-cta"
              className="block"
            >
              <Button 
                className="btn-swipe w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-sm sm:text-lg px-6 sm:px-8 py-5 sm:py-6 rounded-none border-2 border-yellow-500 uppercase tracking-wider"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                <Shield className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Secure Your Copy Now
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Service Bulletins Section Component
const ServiceBulletinsSection = () => {
  return (
    <section className="bg-zinc-950 py-12 sm:py-20 lg:py-32 relative" data-testid="bulletins-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
        <div className="mb-8 sm:mb-16">
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
            <span className="serial-number text-[10px] sm:text-xs">SUPPLEMENTARY MATERIALS // SECTION 03</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            The 5 <span className="text-yellow-500">Service Bulletins</span>
          </h2>
          
          <div className="gold-line mb-4 sm:mb-6" />
          
          <p className="text-sm sm:text-base text-zinc-400 max-w-2xl">
            Printable tactical sheets included with your manual. Post them. Reference them. 
            <span className="text-white"> Execute the protocols.</span>
          </p>
        </div>
        
        {/* Bento Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {serviceBulletins.map((bulletin, index) => {
            const Icon = bulletin.icon;
            
            return (
              <div 
                key={bulletin.id}
                className="bulletin-card p-4 sm:p-6 lg:p-8 corner-marks"
                data-testid={`bulletin-card-${index + 1}`}
              >
                <div className="flex items-start justify-between mb-3 sm:mb-4">
                  <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500" />
                  <span className="serial-number text-[10px] sm:text-xs">{bulletin.id}</span>
                </div>
                
                <h3 
                  className="text-base sm:text-lg lg:text-xl font-bold text-white mb-2 sm:mb-3"
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                >
                  {bulletin.title}
                </h3>
                
                <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed">
                  {bulletin.description}
                </p>
                
                <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-zinc-800">
                  <span className="text-[10px] sm:text-xs text-yellow-500 uppercase tracking-wider">Printable PDF Included</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Mission Section Component
const MissionSection = () => {
  return (
    <section className="relative py-12 sm:py-20 lg:py-32 overflow-hidden" data-testid="mission-section">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url(${IMAGES.heroWalking})`,
          filter: 'brightness(0.4) grayscale(30%)'
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
      
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <Wrench className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
            <span className="serial-number text-[10px] sm:text-xs">TECHNICAL CORRECTION // SECTION 04</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight">
            THE BLUEPRINT: <span className="text-yellow-500">TECHNICAL CORRECTION</span>
          </h2>
          
          <div className="gold-line mb-6 sm:mb-8" />
          
          <div className="space-y-4 sm:space-y-6 text-sm sm:text-base text-zinc-200 leading-relaxed">
            <p>
              The modern fitness industry is built for 20-year-olds with zero miles on their chassis. It either encourages you to rot on the couch or tries to break you with highlight-reel workouts that your current hardware can't support.
            </p>
            
            <p>
              <span className="text-yellow-500 font-bold">Refuse Harmless is the technical correction.</span> We replace "wellness" fluff with professional maintenance.
            </p>
            
            <p>
              This manual is the blueprint for the Dangerous Third Way. We treat your body as high-performance hardware that requires a specific movement protocol to remain an asset. <span className="text-white font-bold">We don't train for vanity; we train for utility.</span> We rebuild the engine you have so you can get back in the game and stay there for the next forty years.
            </p>
            
            <p className="text-xl sm:text-2xl text-yellow-500 font-bold pt-4" style={{ fontFamily: 'Oswald, sans-serif' }}>
              REPAIR THE FRAME. RESTORE THE ASSET.
            </p>
          </div>
          
          <div className="mt-8 sm:mt-12">
            <a 
              href={CTA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mission-cta"
              className="block sm:inline-block"
            >
              <Button 
                className="btn-swipe w-full sm:w-auto bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-sm sm:text-lg px-6 sm:px-8 py-5 sm:py-6 rounded-none border-2 border-yellow-500 uppercase tracking-wider"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                <Shield className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Get The Blueprint
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// FAQ Section Component
const FAQSection = () => {
  return (
    <section className="bg-black py-12 sm:py-20 lg:py-32 relative" data-testid="faq-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <div className="flex items-center justify-center gap-3 mb-4 sm:mb-6">
              <Wrench className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
              <span className="serial-number text-[10px] sm:text-xs">FREQUENTLY ASKED QUESTIONS // SECTION 05</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              <span className="text-yellow-500">Intel</span> Briefing
            </h2>
            
            <div className="gold-line mx-auto" />
          </div>
          
          <Accordion type="single" collapsible className="space-y-3 sm:space-y-4">
            {faqData.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="border border-zinc-800 bg-zinc-900/50 px-4 sm:px-6"
                data-testid={`faq-item-${index + 1}`}
              >
                <AccordionTrigger 
                  className="faq-trigger text-left text-sm sm:text-base lg:text-lg text-white hover:text-yellow-500 hover:no-underline py-4 sm:py-6"
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-xs sm:text-sm text-zinc-400 pb-4 sm:pb-6 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

// Community Section Component
const CommunitySection = () => {
  return (
    <section className="bg-zinc-950 py-12 sm:py-16 border-t border-zinc-800" data-testid="community-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <h3 
            className="text-2xl sm:text-3xl font-bold text-white mb-4"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            JOIN THE <span className="text-yellow-500">@REFUSEHARMLESS</span> TRIBE
          </h3>
          
          <p className="text-sm sm:text-base text-zinc-400 mb-6 sm:mb-8 leading-relaxed">
            Connect with fellow operators. Discuss progress, share advice, get recommendations, 
            hear about deals on future manuals, or just chat with men who refuse to become harmless.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="https://facebook.com/refuseharmless"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-yellow-500 px-6 py-3 transition-all w-full sm:w-auto justify-center"
              data-testid="community-facebook"
            >
              <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <span className="text-white font-medium text-sm uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>Facebook Community</span>
            </a>
            
            <a 
              href="https://instagram.com/refuseharmless"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-yellow-500 px-6 py-3 transition-all w-full sm:w-auto justify-center"
              data-testid="community-instagram"
            >
              <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
              <span className="text-white font-medium text-sm uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>Instagram Community</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-zinc-950 py-8 sm:py-12 border-t border-zinc-800" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex flex-col items-center gap-6 sm:gap-8 md:flex-row md:justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <img 
              src={IMAGES.logo} 
              alt="Refuse Harmless" 
              className="h-12 sm:h-16 w-auto"
            />
          </div>
          
          {/* Social Links */}
          <div className="flex items-center gap-6">
            <a 
              href="https://facebook.com/refuseharmless"
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-400 hover:text-yellow-500 transition-colors"
              data-testid="social-facebook"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a 
              href="https://instagram.com/refuseharmless"
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-400 hover:text-yellow-500 transition-colors"
              data-testid="social-instagram"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
            </a>
          </div>
          
          {/* CTA */}
          <div>
            <a 
              href={CTA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-cta"
            >
              <Button 
                variant="outline"
                className="border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black rounded-none uppercase tracking-wider text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                Get the Manual
              </Button>
            </a>
          </div>
        </div>
        
        {/* Bottom bar */}
        <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-zinc-800 flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
          <p className="text-[10px] sm:text-xs text-zinc-600 text-center sm:text-left">
            © {new Date().getFullYear()} Refuse Harmless. All rights reserved.
          </p>
          <p className="text-[10px] sm:text-xs text-zinc-600 serial-number">
            STAY DANGEROUS. REFUSE THE DECLINE.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App relative">
      {/* Noise overlay */}
      <div className="noise-overlay" />
      
      {/* Header */}
      <Header />
      
      {/* Main content */}
      <main>
        <HeroSection />
        <ProblemSection />
        <ProductSection />
        <ServiceBulletinsSection />
        <MissionSection />
        <FAQSection />
        <CommunitySection />
        <Footer />
      </main>
    </div>
  );
}

export default App;
